/**
 * SONE web — the folder view.
 *
 * What the content area shows when a folder is selected. Craft does this and it
 * is the better arrangement: a folder is a place, and opening a place should
 * show you what is in it. Using a click on the name to expand a tree row
 * instead wastes the one gesture people reach for most, and leaves a folder
 * with nothing to open.
 *
 * Expanding and collapsing stays on the disclosure triangle, which already
 * exists and is the control people expect for it.
 */

import type { ReactElement } from 'react';

import { useT } from '../i18n/useT.tsx';
import { type PageNode } from '../api/client.ts';
import type { EntryCover } from '@sone/core';
import { paths } from '../routes/paths.ts';
import { usePageLink } from '../routes/pageLink.tsx';
import { Breadcrumb } from './Breadcrumb.tsx';
import { EntryCoverHead } from './EntryCover.tsx';
import { EntryIconView, entryKind, titleColorStyle } from './EntryIconView.tsx';
import {
  BrushIcon, FolderPlusIcon, PlusIcon } from './icons.tsx';

interface FolderViewProps {
  folder: PageNode;
  /** Ancestor folders, outermost first, for the breadcrumb. */
  trail: PageNode[];
  /**
   * Absent when this person may not change the folder.
   *
   * Both of them, together: the name is an input and the three buttons make
   * things, and somebody who may only read this folder should be offered
   * neither. They were unconditional, so a viewer got a caret in the title and
   * three buttons that answer 403 — and a share-link visitor got the same, on a
   * folder, which is how "der Ordner wird als Seite dargestellt auf die man
   * schreiben kann" happened (ADR-0092).
   *
   * Optional rather than a `canEdit` flag beside them, so the type says it: a
   * caller with nothing to offer cannot pass a handler that would refuse.
   */
  onCreate?: (parentPageId: string, kind: 'page' | 'folder' | 'canvas') => void;
  onRename?: (pageId: string, title: string) => void;
  /**
   * Give this folder a cover, or take it off with null (ADR-0117).
   *
   * Optional beside the two above and for the same reason: a caller with
   * nothing to offer cannot pass a handler that would be refused.
   *
   * Through a callback rather than a document write, because a folder has none
   * open here — this view renders the tree node it was handed, which is also
   * where the cover it draws comes from.
   */
  onSetCover?: (pageId: string, cover: EntryCover | null) => void;
}

export function FolderView({
  folder,
  trail,
  onCreate,
  onRename,
  onSetCover,
}: FolderViewProps): ReactElement {
  const pageLink = usePageLink();
  const { t } = useT();
  const folders = folder.children.filter((child) => child.kind === 'folder');
  // Everything that is not a folder, rather than everything that says 'page'.
  //
  // A canvas is neither, so it appeared in neither list — a folder holding one
  // showed it as missing rather than as something. The next kind should turn up
  // here without being invited too.
  const pages = folder.children.filter((child) => child.kind !== 'folder');


  return (
    <div className="page-body folder-view">
      {/* The folder's icon and its name, on one line — the same shape and the
          same element a page has (ADR-0030).
        *
        * It was a button that became an input when clicked, on the reasoning
        * that a folder's name should not be edited by accident. Two things were
        * wrong with that. A click on the name of the folder you are already
        * looking at is not "let me see inside", because you are inside; and the
        * swap changed the heading's height, so clicking it nudged everything
        * below down — which is what "it feels unsafe" was.
        *
        * An input at rest, like the page's title. Committed on blur or Enter
        * rather than per keystroke, because there is no document open here to
        * write into. */}
      <EntryCoverHead
        cover={folder.cover ?? null}
        pageId={folder.id}
        {...(onSetCover
          ? { onChange: (next: EntryCover | null) => onSetCover(folder.id, next) }
          : {})}
      >
      {/* Under the cover and above the name (ADR-0164): a cover that runs to
          the top edge is only at the top when it is the first thing in the
          body, and a trail above it pushed the picture down by its own
          height. */}
      <Breadcrumb trail={trail} />

      <div className="entry-heading">
        <span className="entry-heading-icon">
          <EntryIconView icon={folder.icon} kind="folder" />
        </span>
        {onRename ? (
          <input
            className="page-title"
            style={titleColorStyle(folder.icon)}
            // Keyed on the folder, so arriving at a different one replaces the
            // field rather than carrying the previous name into it — an
            // uncontrolled input keeps its own value across a prop change.
            key={folder.id}
            defaultValue={folder.title ?? ''}
            onBlur={(event) => {
              const next = event.currentTarget.value.trim();
              if (next !== folder.title) onRename(folder.id, next);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') event.currentTarget.blur();
              else if (event.key === 'Escape') {
                event.currentTarget.value = folder.title ?? '';
                event.currentTarget.blur();
              }
            }}
            placeholder={t('folder.untitled')}
            aria-label={t('folder.name')}
          />
        ) : (
          // A heading, not a disabled field. A greyed-out input still says "this
          // is where you type", and a folder somebody may only read has nothing
          // for them to type into.
          <h1 className="page-title" style={titleColorStyle(folder.icon)}>
            {folder.title || t('folder.untitled')}
          </h1>
        )}
      </div>
      </EntryCoverHead>

      {onCreate && (
        <div className="folder-actions">
          <button type="button" className="btn" onClick={() => onCreate(folder.id, 'page')}>
            <PlusIcon /> {t('entry.newPage')}
          </button>
          <button type="button" className="btn" onClick={() => onCreate(folder.id, 'folder')}>
            <FolderPlusIcon /> {t('entry.newFolder')}
          </button>
          <button
            className="btn"
            type="button"
            onClick={() => onCreate(folder.id, 'canvas')}
          >
            <BrushIcon /> {t('canvas.new')}
          </button>
        </div>
      )}

      {/* No "Add columns" here any more.
        *
        * A folder organises; a collection is content and belongs in a page
        * (ADR-0021). Turning a folder into a table made a folder mean two
        * things, and put every row in the sidebar. */}
      {folder.children.length === 0 ? (
        <p className="muted folder-empty">
          {t('folder.empty')}
        </p>
      ) : (
        <>
          {/* The overview groups entry kinds into sections; each section keeps
              the saved order used by the sidebar. */}
          {folders.length > 0 && (
            <section>
              <h2 className="folder-section">{t('folder.folders')}</h2>
              <ul className="folder-list">
                {folders.map((child) => (
                  <li key={child.id}>
                    <a href={pageLink(child.id, child.title ?? undefined)}>
                      {/* The child's own icon and colour, for the same reason
                          the heading has them: a folder decorated in the tree
                          that is drawn plain in its parent's listing looks like
                          the decoration only took in one place. */}
                      <EntryIconView icon={child.icon} kind="folder" />
                      <span
                        className="folder-list-name"
                        style={titleColorStyle(child.icon)}
                      >
                        {child.title || t('folder.untitled')}
                      </span>
                      <span className="folder-list-meta">
                        {describeContents(child)}
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {pages.length > 0 && (
            <section>
              <h2 className="folder-section">{t('folder.pages')}</h2>
              <ul className="folder-list">
                {pages.map((child) => (
                  <li key={child.id}>
                    <a href={pageLink(child.id, child.title ?? undefined)}>
                      <EntryIconView icon={child.icon} kind={entryKind(child.kind)} />
                      <span
                        className="folder-list-name"
                        style={titleColorStyle(child.icon)}
                      >
                        {child.title || t('entry.untitled')}
                      </span>
                      <span className="folder-list-meta">
                        {formatEdited(child.lastEditedAt)}
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </div>
  );
}

/** "3 pages · 1 folder", or "Empty". Says whether it is worth opening. */
function describeContents(folder: PageNode): string {
  const pages = folder.children.filter((child) => child.kind === 'page').length;
  const folders = folder.children.filter((child) => child.kind === 'folder').length;
  const parts: string[] = [];
  if (pages > 0) parts.push(`${pages} ${pages === 1 ? 'page' : 'pages'}`);
  if (folders > 0) parts.push(`${folders} ${folders === 1 ? 'folder' : 'folders'}`);
  return parts.length > 0 ? parts.join(' · ') : 'Empty';
}

/**
 * Relative for recent edits, absolute beyond a week.
 *
 * "3 days ago" is what someone wants for something they touched recently, and
 * useless for something from March.
 */
function formatEdited(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';

  const days = Math.floor((Date.now() - then) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  return new Date(then).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}
